import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import Footer from '../components/Footer';
import logoImage from '../assets/Image.png';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showUserTypeModal, setShowUserTypeModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [resetMessage, setResetMessage] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setValidationErrors((prev) => ({
        ...prev,
        email: 'Please enter your email address first'
      }));
      return;
    }
    setResetLoading(true);
    setResetMessage(null);
    try {
      await sendPasswordResetEmail(auth, formData.email);
      setResetMessage({
        type: 'success',
        text: 'Password reset email sent! Please check your inbox.'
      });
      setTimeout(() => setResetMessage(null), 5000);
    } catch (err) {
      let errorMessage = 'Failed to send reset email. ';
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      }
      setResetMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleUserTypeSelection = (type) => {
    setShowUserTypeModal(false);
    navigate(`/register?type=${type}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError('');
    
    try {
      // Normal Firebase authentication for all users
      const { user } = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      let userData = null;
      if (userDoc.exists()) {
        userData = userDoc.data();
      }

      if (!userData) {
        throw new Error('User profile not found');
      }
      
      setTimeout(() => {
        switch (userData?.userType) {
          case 'superadmin':
            navigate('/superadmin/dashboard', { replace: true });
            break;
          case 'team':
            navigate('/team/dashboard', { replace: true });
            break;
          case 'agent':
            navigate('/agent/dashboard', { replace: true });
            break;
          case 'franchise':
            navigate('/franchise/dashboard', { replace: true });
            break;
          case 'employer':
            navigate('/dashboard/employer', { replace: true });
            break;
          case 'jobseeker':
            navigate('/dashboard/jobseeker', { replace: true });
            break;
          default:
            navigate('/', { replace: true });
        }
      }, 100);
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up first.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Invalid email or password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Animated Gradient Background */}
      <div className="animated-bg">
        <div className="gradient-sphere sphere-1"></div>
        <div className="gradient-sphere sphere-2"></div>
        <div className="gradient-sphere sphere-3"></div>
        <div className="gradient-sphere sphere-4"></div>
      </div>

      <div className="content-wrapper">
        <div className="login-card-wrapper">
          {/* Logo Section with Animation */}
          <div className="logo-section">
            <div className="logo-wrapper">
              <div className="logo-glow"></div>
              <div className="logo-container">
                <img src={logoImage} alt="Logo" className="logo-image" />
              </div>
            </div>
            <h1 className="welcome-title">Harshal Jobs Gateway</h1>
            <p className="welcome-subtitle">Access executive search tools & custom career opportunities.</p>
          </div>

          {/* Login Card */}
          <div className="login-card">
            <form onSubmit={handleSubmit} className="login-form">
              {/* Email Field */}
              <div className="form-group">
                <label className="form-label">Access Identifier (Email)</label>
                <div className={`input-wrapper ${focusedField === 'email' ? 'input-focused' : ''}`}>
                  <div className="input-icon">
                    <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="username@organization.com"
                    className={`form-input ${validationErrors.email ? 'input-error' : ''}`}
                  />
                </div>
                {validationErrors.email && (
                  <p className="error-message">{validationErrors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="form-group">
                <label className="form-label">Secure Access Key</label>
                <div className={`input-wrapper ${focusedField === 'password' ? 'input-focused' : ''}`}>
                  <div className="input-icon">
                    <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter security passcode"
                    className={`form-input ${validationErrors.password ? 'input-error' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="password-toggle"
                  >
                    {showPassword ? (
                      <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    ) : (
                      <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    )}
                  </button>
                </div>
                {validationErrors.password && (
                  <p className="error-message">{validationErrors.password}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="options-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">Retain key on this device</span>
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={resetLoading}
                  className="forgot-password-btn"
                >
                  {resetLoading ? (
                    <span className="loading-dots">•••</span>
                  ) : (
                    'Recover Access Key?'
                  )}
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="error-alert">
                  <svg className="alert-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p>{error}</p>
                </div>
              )}

              {/* Reset Message */}
              {resetMessage && (
                <div className={`reset-alert ${resetMessage.type}`}>
                  <svg className="alert-icon" fill="currentColor" viewBox="0 0 20 20">
                    {resetMessage.type === 'success' ? (
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    ) : (
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    )}
                  </svg>
                  <p>{resetMessage.text}</p>
                </div>
              )}

              {/* Submit Button */}
              <button type="submit" disabled={loading} className="submit-btn">
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Authenticating...
                  </>
                ) : (
                  <>
                    Authenticate Session
                    <svg className="arrow-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="divider">
              <div className="divider-line"></div>
              <span className="divider-text">Begin your membership</span>
              <div className="divider-line"></div>
            </div>

            {/* Create Account Button */}
            <button onClick={() => setShowUserTypeModal(true)} className="create-account-btn">
              <svg className="create-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Establish Profile / Register
            </button>
          </div>

          {/* Terms Section - Centered */}
          <div className="terms-section">
            <span>© 2024 Harshal Jobs. All rights reserved.</span>
          </div>
        </div>
      </div>

      <Footer />

      {/* User Type Selection Modal */}
      {showUserTypeModal && (
        <div className="modal-overlay" onClick={() => setShowUserTypeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowUserTypeModal(false)} className="modal-close">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="modal-header">
              <div className="modal-icon">
                <svg className="modal-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
              <h2 className="modal-title">Establish New Profile</h2>
              <p className="modal-subtitle">Select profile category to initiate registration</p>
            </div>

            <div className="modal-options">
              <button onClick={() => handleUserTypeSelection('jobseeker')} className="option-btn">
                <div className="option-icon jobseeker-icon">
                  <svg className="option-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="option-text">
                  <h3>Talent Candidate</h3>
                  <p>Explore prime career placements and submit credentials</p>
                </div>
                <svg className="option-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button onClick={() => handleUserTypeSelection('employer')} className="option-btn">
                <div className="option-icon employer-icon">
                  <svg className="option-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="option-text">
                  <h3>Corporate Employer</h3>
                  <p>Publish open positions and manage acquisition pipelines</p>
                </div>
                <svg className="option-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Container Styles - Deep Blue Theme */
        .login-container {
          position: relative;
          min-height: 100vh;
          background: linear-gradient(135deg, #0e3a61 0%, #051728 100%);
          overflow-x: hidden;
        }

        /* Animated Gradient Background */
        .animated-bg {
          position: absolute;
          inset: 0;
          overflow: hidden;
          z-index: 1;
        }

        .gradient-sphere {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.4;
          animation: float 20s ease-in-out infinite;
        }

        .sphere-1 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.02));
          top: -150px;
          left: -150px;
          animation-delay: 0s;
        }

        .sphere-2 {
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(226, 130, 47, 0.1), rgba(226, 130, 47, 0.01));
          bottom: -200px;
          right: -200px;
          animation-delay: -5s;
        }

        .sphere-3 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(14, 58, 97, 0.3), rgba(14, 58, 97, 0.05));
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: -10s;
        }

        .sphere-4 {
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.02));
          top: 20%;
          right: 10%;
          animation-delay: -7s;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.05);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.98);
          }
        }

        /* Content Wrapper */
        .content-wrapper {
          position: relative;
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 2rem;
        }

        .login-card-wrapper {
          width: 100%;
          max-width: 480px;
        }

        /* Logo Section */
        .logo-section {
          text-align: center;
          margin-bottom: 2rem;
          animation: fadeInUp 0.6s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .logo-wrapper {
          position: relative;
          width: 80px;
          height: 80px;
          margin: 0 auto 1.25rem;
        }

        .logo-glow {
          position: absolute;
          inset: -3px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.8), transparent);
          animation: pulseGlow 2.5s ease-in-out infinite;
        }

        @keyframes pulseGlow {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.9;
            transform: scale(1.08);
          }
        }

        .logo-container {
          position: relative;
          width: 100%;
          height: 100%;
          background: linear-gradient(145deg, #ffffff 0%, #f0f0f0 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          transition: transform 0.3s ease;
        }

        .logo-container:hover {
          transform: scale(1.02);
        }

        .logo-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 50%;
        }

        .welcome-title {
          font-size: 1.875rem;
          font-weight: 700;
          background: linear-gradient(135deg, #ffffff 0%, #dbeafe 100%);
          background-clip: text;
          -webkit-background-clip: text;
          color: transparent;
          margin-bottom: 0.5rem;
          letter-spacing: -0.02em;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .welcome-subtitle {
          color: rgba(255, 255, 255, 0.95);
          font-size: 0.95rem;
          font-weight: 400;
          max-width: 28rem;
          margin: 0 auto;
          line-height: 1.7;
        }

        /* Login Card - Premium Asymmetric Geometry */
        .login-card {
          position: relative;
          background: #ffffff;
          border-radius: 2rem 3.5rem 2rem 3.5rem;
          padding: 2.5rem;
          box-shadow: 0 35px 80px -30px rgba(14, 58, 97, 0.3);
          animation: fadeInUp 0.6s ease-out 0.1s both;
          overflow: hidden;
          border: 1px solid rgba(14, 58, 97, 0.08);
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        /* Form Elements */
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #4a5568;
          letter-spacing: -0.01em;
        }

        .input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #a0aec0;
          transition: all 0.2s ease;
        }

        .input-focused .input-icon {
          color: #0e3a61;
        }

        .form-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.75rem;
          border-radius: 1rem;
          border: 1.5px solid #e2e8f0;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #ffffff;
          color: #2d3748;
          font-weight: 450;
        }

        .form-input:focus {
          outline: none;
          border-color: #0e3a61;
          box-shadow: 0 0 0 4px rgba(14, 58, 97, 0.10);
        }

        .form-input::placeholder {
          color: #cbd5e1;
          font-weight: 400;
        }

        .input-error {
          border-color: #f56565;
        }

        .input-error:focus {
          border-color: #f56565;
          box-shadow: 0 0 0 4px rgba(245, 101, 101, 0.1);
        }

        .password-toggle {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #a0aec0;
          padding: 0;
          display: flex;
          transition: color 0.2s;
        }

        .password-toggle:hover {
          color: #0e3a61;
        }

        .icon-svg {
          width: 1.125rem;
          height: 1.125rem;
        }

        .error-message {
          color: #f56565;
          font-size: 0.7rem;
          margin-top: 0.25rem;
          font-weight: 500;
        }

        /* Options Row */
        .options-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }

        .checkbox-input {
          width: 1rem;
          height: 1rem;
          border-radius: 0.25rem;
          cursor: pointer;
          accent-color: #0e3a61;
        }

        .checkbox-text {
          font-size: 0.813rem;
          color: #4a5568;
          font-weight: 450;
        }

        .forgot-password-btn {
          background: none;
          border: none;
          color: #0e3a61;
          font-size: 0.813rem;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }

        .forgot-password-btn:hover {
          color: #e2822f;
          text-decoration: underline;
        }

        .loading-dots {
          animation: pulse 1.5s ease-in-out infinite;
        }

        /* Alerts */
        .error-alert {
          background: #fff5f5;
          border: 1px solid #fed7d7;
          border-radius: 1rem;
          padding: 0.75rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #e53e3e;
          font-size: 0.813rem;
          font-weight: 500;
        }

        .reset-alert {
          border-radius: 1rem;
          padding: 0.75rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          animation: slideDown 0.3s ease-out;
          font-size: 0.813rem;
          font-weight: 500;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .reset-alert.success {
          background: #f0fff4;
          border: 1px solid #c6f6d5;
          color: #38a169;
        }

        .reset-alert.error {
          background: #fffff0;
          border: 1px solid #fefcbf;
          color: #d69e2e;
        }

        .alert-icon {
          width: 1rem;
          height: 1rem;
          flex-shrink: 0;
        }

        /* Submit Button */
        .submit-btn {
          width: 100%;
          padding: 0.875rem;
          border-radius: 1rem;
          background: linear-gradient(135deg, #0e3a61 0%, #1a5c96 100%);
          color: white;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.625rem;
          margin-top: 0.5rem;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 15px 25px -10px rgba(14, 58, 97, 0.4);
        }

        .submit-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .submit-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .spinner {
          width: 1rem;
          height: 1rem;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .arrow-icon {
          width: 1rem;
          height: 1rem;
          transition: transform 0.2s;
        }

        .submit-btn:hover .arrow-icon {
          transform: translateX(4px);
        }

        /* Divider */
        .divider {
          display: flex;
          align-items: center;
          margin: 1.5rem 0;
        }

        .divider-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
        }

        .divider-text {
          padding: 0 1rem;
          font-size: 0.7rem;
          color: #a0aec0;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Create Account Button */
        .create-account-btn {
          width: 100%;
          padding: 0.875rem;
          border-radius: 1rem;
          border: 1.5px solid #e2e8f0;
          background: #ffffff;
          color: #0e3a61;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.625rem;
        }

        .create-account-btn:hover {
          border-color: #0e3a61;
          background: #f8fafc;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px -5px rgba(0, 0, 0, 0.1);
        }

        .create-icon {
          width: 1rem;
          height: 1rem;
        }

        /* Terms Section - Centered */
        .terms-section {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.25rem;
          margin-top: 1.5rem;
          font-size: 0.7rem;
          color: rgba(255, 255, 255, 0.9);
          text-align: center;
          line-height: 1.5;
          width: 100%;
        }

        .terms-section span {
          font-size: 0.7rem;
          color: rgba(255, 255, 255, 0.9);
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(5, 23, 40, 0.8);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          padding: 1rem;
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          background: white;
          border-radius: 2rem 3.5rem 2rem 3.5rem;
          padding: 2.5rem;
          max-width: 440px;
          width: 100%;
          position: relative;
          animation: modalScale 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 50px 70px -20px rgba(14, 58, 97, 0.4);
          border: 1px solid rgba(14, 58, 97, 0.08);
        }

        @keyframes modalScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .modal-close {
          position: absolute;
          top: 1.25rem;
          right: 1.25rem;
          background: none;
          border: none;
          cursor: pointer;
          color: #a0aec0;
          padding: 0.25rem;
          display: flex;
          transition: color 0.2s;
        }

        .modal-close:hover {
          color: #2d3748;
        }

        .modal-close svg {
          width: 1.25rem;
          height: 1.25rem;
        }

        .modal-header {
          text-align: center;
          margin-bottom: 1.75rem;
        }

        .modal-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(145deg, #0e3a61 0%, #1a5c96 100%);
          border-radius: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          box-shadow: 0 10px 20px -5px rgba(14, 58, 97, 0.3);
        }

        .modal-icon-svg {
          width: 32px;
          height: 32px;
          color: white;
        }

        .modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 0.25rem;
          letter-spacing: -0.02em;
        }

        .modal-subtitle {
          color: #718096;
          font-size: 0.813rem;
        }

        .modal-options {
          display: flex;
          flex-direction: column;
          gap: 0.875rem;
        }

        .option-btn {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.25rem;
          border: 1.5px solid #e2e8f0;
          border-radius: 1.25rem;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
          width: 100%;
          text-align: left;
        }

        .option-btn:hover {
          border-color: #0e3a61;
          background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
          transform: translateX(6px);
          box-shadow: 0 8px 20px -8px rgba(14, 58, 97, 0.25);
        }

        .option-icon {
          width: 48px;
          height: 48px;
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .option-icon-svg {
          width: 24px;
          height: 24px;
          color: white;
        }

        .jobseeker-icon {
          background: linear-gradient(145deg, #0e3a61 0%, #1a5c96 100%);
        }

        .employer-icon {
          background: linear-gradient(145deg, #e2822f 0%, #b96a25 100%);
        }

        .option-text {
          flex: 1;
        }

        .option-text h3 {
          font-size: 1rem;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 0.125rem;
        }

        .option-text p {
          font-size: 0.75rem;
          color: #718096;
        }

        .option-arrow {
          width: 1.125rem;
          height: 1.125rem;
          color: #cbd5e1;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .option-btn:hover .option-arrow {
          transform: translateX(6px);
          color: #0e3a61;
        }

        /* Responsive */
        @media (max-width: 640px) {
          .content-wrapper {
            padding: 1rem;
          }
          
          .login-card {
            padding: 1.5rem;
            border-radius: 1.5rem;
          }
          
          .welcome-title {
            font-size: 1.5rem;
          }
          
          .modal-content {
            padding: 1.5rem;
            margin: 1rem;
          }
          
          .logo-wrapper {
            width: 64px;
            height: 64px;
          }
          
          .modal-title {
            font-size: 1.25rem;
          }
          
          .option-btn {
            padding: 0.875rem 1rem;
          }
          
          .option-icon {
            width: 42px;
            height: 42px;
          }

          .terms-section {
            font-size: 0.65rem;
            gap: 0.2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;